import { useRef, useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/track";

const INVESTMENT = ["Up to ₹5 lakh", "₹5–8 lakh", "₹8–10 lakh", "₹10 lakh+", "Need Guidance"];
const EXPERIENCE = ["First Business", "Existing Business Owner", "Food & Beverage", "Retail", "Investor", "Other"];

const STATES = [
  "Andhra Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra",
  "Odisha", "Punjab", "Rajasthan", "Tamil Nadu", "Telangana", "Uttar Pradesh", "Uttarakhand",
  "West Bengal", "Other",
];

const label = "font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-deep/60";
const input =
  "h-12 w-full rounded-xl border border-cream-line bg-white px-4 text-base font-medium text-deep placeholder:text-deep/35 outline-none transition-all focus:border-orange-rific focus:ring-2 focus:ring-orange-rific/25";

const Field = ({ name, label: text, children, required, optional, error }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={`lead-${name}`} className={label}>
      {text} {required ? <span className="text-orange-rific">*</span> : optional ? <span className="text-deep/40">· optional</span> : null}
    </label>
    {children}
    {error && (
      <p className="text-xs font-semibold text-red-600" data-testid={`franchise-form-error-${name}`}>
        {error}
      </p>
    )}
  </div>
);

export default function LeadForm({ id = "franchise-form" }) {
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", city: "", state: "", has_location: "",
    preferred_location: "", shop_area: "", investment_range: "", experience: "", message: "", consent: false,
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const started = useRef(false);
  const inFlight = useRef(false);

  const needsLocation = form.has_location === "Yes";
  const set = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: val }));
    setFieldErrors((fe) => {
      if (!fe[key]) return fe;
      const next = { ...fe };
      delete next[key];
      return next;
    });
  };

  const onStart = () => {
    if (!started.current) {
      started.current = true;
      track("franchise_form_start");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    if (!form.has_location) {
      setError("Please tell us whether you have a location.");
      return;
    }
    inFlight.current = true;
    setError("");
    setFieldErrors({});
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      track("franchise_form_submit", { investment_range: form.investment_range, has_location: form.has_location });
      track("franchise_form_success", { investment_range: form.investment_range, has_location: form.has_location });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (Array.isArray(d)) {
        const fe = {};
        d.forEach((x) => {
          const f = x?.loc?.[x.loc.length - 1];
          if (typeof f === "string" && !fe[f]) {
            fe[f] = (x.msg || "Invalid value").replace(/^Value error,\s*/i, "").replace(/^String should have at least \d+ characters$/i, "This field is required");
          }
        });
        setFieldErrors(fe);
      } else {
        setError(typeof d === "string" ? d : "Something went wrong. Please try again.");
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="franchise-form-success" className="rounded-3xl bg-deep px-6 py-14 text-center md:px-12">
        <span className="mx-auto mb-6 grid h-14 w-14 place-items-center rounded-full bg-orange-rific">
          <Check className="h-7 w-7 text-white" />
        </span>
        <p className="font-display text-4xl font-extrabold tracking-[-0.01em] text-cream md:text-5xl">THANK YOU.</p>
        <p className="mt-3 font-display text-xl font-bold uppercase tracking-[-0.01em] text-cream md:text-2xl">
          Your RIFIC franchise application has been received.
        </p>
        <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-cream/75">
          Our team will review your details and contact you regarding the opportunity.
        </p>
      </div>
    );
  }

  return (
    <form
      id={id}
      onSubmit={submit}
      onFocus={onStart}
      noValidate={true}
      data-testid="franchise-lead-form"
      className="rounded-3xl border border-cream-line bg-white p-6 shadow-[0_24px_60px_-32px_rgba(7,61,43,0.25)] md:p-10"
    >
      <div className="grid gap-5 md:grid-cols-2">
        <Field name="name" label="Full Name" required error={fieldErrors.name}>
          <input id="lead-name" data-testid="franchise-form-name-input" className={input} required value={form.name} onChange={set("name")} placeholder="Your full name" />
        </Field>
        <Field name="mobile" label="Mobile Number" required error={fieldErrors.mobile}>
          <input id="lead-mobile" data-testid="franchise-form-mobile-input" className={input} required type="tel" inputMode="tel" value={form.mobile} onChange={set("mobile")} placeholder="10-digit mobile number" />
        </Field>
        <Field name="city" label="City" required error={fieldErrors.city}>
          <input id="lead-city" data-testid="franchise-form-city-input" className={input} required value={form.city} onChange={set("city")} placeholder="City" />
        </Field>
        <Field name="state" label="State" required error={fieldErrors.state}>
          <input id="lead-state" data-testid="franchise-form-state-input" className={input} required list="rific-states" value={form.state} onChange={set("state")} placeholder="State" />
          <datalist id="rific-states">{STATES.map((s) => <option key={s} value={s} />)}</datalist>
        </Field>
        <Field name="investment_range" label="Investment Readiness" required error={fieldErrors.investment_range}>
          <select id="lead-investment" data-testid="franchise-form-investment-select" className={input} required value={form.investment_range} onChange={set("investment_range")}>
            <option value="">Select readiness</option>
            {INVESTMENT.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>

        <div className="md:col-span-2">
          <Field name="has_location" label="Do you have a location?" required error={fieldErrors.has_location}>
            <div className="flex gap-3" role="radiogroup" aria-label="Do you have a location?" data-testid="franchise-form-location-group">
              {["Yes", "No"].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  role="radio"
                  aria-checked={form.has_location === opt}
                  data-testid={`franchise-form-location-${opt.toLowerCase()}`}
                  onClick={() => {
                    setForm((f) => ({ ...f, has_location: opt }));
                    setFieldErrors((fe) => { const n = { ...fe }; delete n.has_location; return n; });
                  }}
                  className={`h-12 flex-1 rounded-xl border text-sm font-bold uppercase tracking-[0.14em] transition-all ${
                    form.has_location === opt
                      ? "border-orange-rific bg-orange-rific/10 text-orange-rific"
                      : "border-cream-line bg-white text-deep/60 hover:border-deep/30"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </Field>
        </div>

        {needsLocation && (
          <>
            <Field name="preferred_location" label="Preferred Location" error={fieldErrors.preferred_location}>
              <input id="lead-prefloc" data-testid="franchise-form-location-input" className={input} value={form.preferred_location} onChange={set("preferred_location")} placeholder="Area / locality / city" />
            </Field>
            <Field name="shop_area" label="Available Shop Area" error={fieldErrors.shop_area}>
              <input id="lead-area" data-testid="franchise-form-area-input" className={input} value={form.shop_area} onChange={set("shop_area")} placeholder="e.g. 250 sq ft on high street" />
            </Field>
          </>
        )}

        <Field name="email" label="Email" optional error={fieldErrors.email}>
          <input id="lead-email" data-testid="franchise-form-email-input" className={input} type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
        </Field>
        <Field name="experience" label="Business Experience" optional error={fieldErrors.experience}>
          <select id="lead-exp" data-testid="franchise-form-experience-select" className={input} value={form.experience} onChange={set("experience")}>
            <option value="">Select experience</option>
            {EXPERIENCE.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>

        <div className="md:col-span-2">
          <Field name="message" label="Message" optional error={fieldErrors.message}>
            <textarea id="lead-msg" data-testid="franchise-form-message-input" className="min-h-24 w-full rounded-xl border border-cream-line bg-white px-4 py-3 text-base font-medium text-deep placeholder:text-deep/35 outline-none transition-all focus:border-orange-rific focus:ring-2 focus:ring-orange-rific/25" value={form.message} onChange={set("message")} placeholder="Tell us about your city, proposed location and plans" />
          </Field>
        </div>
      </div>

      <label className="mt-6 flex cursor-pointer items-start gap-3" data-testid="franchise-form-consent-label">
        <input
          data-testid="franchise-form-consent-checkbox"
          type="checkbox"
          required
          checked={form.consent}
          onChange={set("consent")}
          className="mt-1 h-5 w-5 shrink-0 accent-orange-rific"
        />
        <span className="text-base leading-relaxed text-deep/80">
          I agree to be contacted by RIFIC regarding franchise opportunities. <span className="text-orange-rific">*</span>
        </span>
      </label>
      {fieldErrors.consent && (
        <p className="mt-2 text-xs font-semibold text-red-600" data-testid="franchise-form-error-consent">
          {fieldErrors.consent}
        </p>
      )}

      {error && (
        <p data-testid="franchise-form-error" className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {error}
        </p>
      )}

      <button
        type="submit"
        data-testid="franchise-form-submit-button"
        disabled={submitting}
        className="mt-7 inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-orange-rific px-8 text-sm font-bold uppercase tracking-[0.16em] text-white transition-all duration-300 hover:bg-deep disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
      >
        {submitting ? "SUBMITTING…" : "Submit Franchise Application"}
        {!submitting && <ArrowRight className="h-4 w-4" />}
      </button>
    </form>
  );
}
